import React, {useEffect, useState} from "react";
import axios from "axios";
import {Key, ShieldCheck} from "lucide-react";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import DataTable from "react-data-table-component";
import {customStyles} from "../../utils/DataTableCustomStyle";

/**
 * Passcode generation — redesigned in the design-system idiom.
 * Business logic preserved 1:1:
 *   GET  /users/getAllClientUsers
 *   GET  /asset-management/user/{employeeId}
 *   POST /passcodes/generate
 *   POST /passcodes/generate/bulk
 * Only the chrome changes.
 */
export default function PasscodeGeneration() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [assignedAssets, setAssignedAssets] = useState([]);
  const [generatingBulk, setGeneratingBulk] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);

  const fetchEmployees = async () => {
    try {
      const url = `${Constants.API_V1}/${getClientIdOfUser()}/users/getAllClientUsers`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(`${Constants.BASE_URL}${url}`);
      setEmployees(res.data.responseData || []);
    } catch {
      showToast("Error loading employees", {type: "error"});
      setEmployees([]);
    }
  };

  const fetchAssignedAssets = async () => {
    if (!selectedEmployeeId) { setAssignedAssets([]); return; }
    try {
      const url = `${Constants.API_V1}/${getClientIdOfUser()}/asset-management/user/${selectedEmployeeId}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(`${Constants.BASE_URL}${url}`);
      const raw = Array.isArray(res.data) ? res.data : [];
      const activeAssignments = raw.filter((a) => !a.returnDate);
      const mapped = activeAssignments.map((a) => ({
        assignmentId: a.assignmentId,
        assetId: a.asset?.assetId,
        assetName: a.asset?.name,
        assetUnitId: a.asset?.assetUnitId,
      }));
      const unique = Object.values(
        mapped.reduce((acc, curr) => {
          if (!acc[curr.assetId]) acc[curr.assetId] = curr;
          return acc;
        }, {})
      );
      setAssignedAssets(unique);
    } catch {
      showToast("Error loading assigned assets", {type: "error"});
      setAssignedAssets([]);
    }
  };

  const generateSinglePasscode = async (assetId) => {
    try {
      setGeneratingFor(assetId);
      const requesterId = JSON.parse(localStorage.getItem(Constants.LOGGED_IN_USER)).userId;
      const url = `${Constants.API_V1}/${getClientIdOfUser()}/passcodes/generate`;
      await axios.post(`${Constants.BASE_URL}${url}`, {requesterId, assetId, userId: selectedEmployeeId});
      showToast("Passcode generated", {type: "success"});
    } catch {
      showToast("Failed to generate passcode", {type: "error"});
    } finally {
      setGeneratingFor(null);
    }
  };

  const generateBulkPasscodes = async () => {
    try {
      setGeneratingBulk(true);
      const requesterId = JSON.parse(localStorage.getItem(Constants.LOGGED_IN_USER)).userId;
      const url = `${Constants.API_V1}/${getClientIdOfUser()}/passcodes/generate/bulk`;
      await axios.post(`${Constants.BASE_URL}${url}`, {requesterId, userIds: [Number(selectedEmployeeId)]});
      showToast("Bulk passcodes generated", {type: "success"});
    } catch {
      showToast("Failed to generate passcodes", {type: "error"});
    } finally {
      setGeneratingBulk(false);
    }
  };

  useEffect(() => { fetchEmployees(); }, []);
  useEffect(() => { fetchAssignedAssets(); /* eslint-disable-next-line */ }, [selectedEmployeeId]);

  const columns = [
    {name: "Asset ID",   selector: (row) => row.assetId,     sortable: true, width: "110px"},
    {name: "Name",       selector: (row) => row.assetName,   sortable: true, wrap: true},
    {name: "Unit ID",    selector: (row) => row.assetUnitId, sortable: true, width: "160px"},
    {
      name: "",
      width: "180px",
      right: true,
      ignoreRowClick: true,
      cell: (row) => {
        const busy = generatingFor === row.assetId;
        return (
          <button
            onClick={() => generateSinglePasscode(row.assetId)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md bg-brand-700 text-white hover:bg-brand-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <Key className="h-3.5 w-3.5" />
            {busy ? "Generating…" : "Generate passcode"}
          </button>
        );
      },
    },
  ];

  const empOptionLabel = (u) => u.fullName || u.username;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-ink-200 rounded-xl shadow-xs">
        <div className="p-5 border-b border-ink-100 flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-50 grid place-items-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-brand-700" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink-900">Select an employee</h2>
            <p className="text-sm text-ink-500 mt-0.5">
              Generate one-time unlock codes for their assigned Bluetooth locks.
            </p>
          </div>
        </div>

        <div className="p-5">
          <label htmlFor="passcode-employee" className="block text-xs font-medium text-ink-500 mb-1.5">
            Employee
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <select
              id="passcode-employee"
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              className="flex-1 min-w-0 h-10 px-3 pr-8 text-base rounded-md bg-white border border-ink-200 shadow-inset text-ink-900 hover:border-ink-300 focus:border-brand-500 focus:outline-none focus:ring"
            >
              <option value="">Select an employee…</option>
              {employees.map((u) => (
                <option key={u.userId} value={u.userId}>{empOptionLabel(u)}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={generateBulkPasscodes}
              disabled={!selectedEmployeeId || assignedAssets.length === 0 || generatingBulk}
              className="shrink-0 inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-medium rounded-md bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              <Key className="h-4 w-4" />
              {generatingBulk ? "Generating…" : "Generate for all assigned"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-ink-200 rounded-xl shadow-xs overflow-hidden">
        <div className="px-5 py-3 border-b border-ink-100 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-base font-semibold text-ink-900">
            Assigned assets {assignedAssets.length > 0 && (
              <span className="ml-2 text-sm font-medium text-ink-500 tabular-nums">({assignedAssets.length})</span>
            )}
          </h3>
        </div>
        <div className="allocate-table-wrapper">
          {!selectedEmployeeId ? (
            <div className="py-10 px-6 text-center text-sm text-ink-500">
              Pick an employee above to see their assigned BT locks.
            </div>
          ) : assignedAssets.length === 0 ? (
            <div className="py-10 px-6 text-center text-sm text-ink-500">
              This employee has no active asset assignments.
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={assignedAssets}
              pagination
              customStyles={customStyles}
            />
          )}
        </div>
      </div>
    </div>
  );
}
